import { DropdownMenuGroup } from '@radix-ui/react-dropdown-menu';
import { Typography } from '@remarkable/ark-web';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import type { EnterpriseDomain } from 'src/api/endpoints/cloudEnterpriseAdminApi.types';
import {
  useDeleteDomain,
  useMakePrimaryDomain,
} from 'src/api/queries/enterpriseAdmin';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { Button, Input, Modal } from 'src/components';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
} from 'src/components/DropdownMenu';
import { userHelpers } from 'src/utils/userHelpers';

export function DomainEditMenu({
  domain,
  children,
}: {
  domain: EnterpriseDomain;
  children?: React.ReactNode;
}) {
  const accessTokenClaims = useAccessTokenClaims();
  const [open, setOpen] = useState<boolean>(false);
  const deleteDomain = useDeleteDomain();
  const makePrimaryDomain = useMakePrimaryDomain();

  if (!userHelpers.hasPermissionUpdateOrgSettings(accessTokenClaims.data)) {
    return null;
  }

  const handleDeletion = () => {
    if (!domain.address) {
      toast.error('Domain address is required');
      return;
    }
    deleteDomain.mutate(
      {
        domainId: domain.address,
      },
      { onSettled: () => setOpen(false) }
    );
  };

  const handleMakePrimary = () => {
    if (!domain.address) {
      toast.error('Domain address is required');
      return;
    }
    makePrimaryDomain.mutate({
      domainId: domain.address,
    });
  };

  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu trigger={children}>
        <DropdownMenuGroup>
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem onClick={handleMakePrimary}>
            Make primary
          </DropdownMenuItem>
          <Modal.Trigger asChild>
            <DropdownMenuItem>Delete</DropdownMenuItem>
          </Modal.Trigger>
        </DropdownMenuGroup>
      </DropdownMenu>
      <ConfirmDeleteDomainModal
        domain={domain}
        handleDeletion={handleDeletion}
      />
    </Modal.Root>
  );
}

const ConfirmDeleteDomainModal = ({
  domain,
  handleDeletion,
}: {
  domain: EnterpriseDomain;
  handleDeletion: () => void;
}) => {
  const [confirmInput, setConfirmInput] = useState('');
  const isConfirmed = confirmInput.toLowerCase() === 'delete domain';
  const deleteDomain = useDeleteDomain();

  const handleConfirmClick = () => {
    setConfirmInput('');
    handleDeletion();
  };

  return (
    <Modal.Content title="Delete domain?">
      <>
        <div className="flex flex-col gap-y-24 lm:gap-y-40">
          <div>
            <Typography variant="body-lg" as="div">
              Are you sure you want to delete the following domain?
              <br />
              <br />
              <b>{domain.address}</b>
            </Typography>
          </div>
          <Input
            label={
              <Typography variant="body-md" className="text-left">
                Please type <b>delete domain</b> to confirm
              </Typography>
            }
            value={confirmInput}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setConfirmInput(e.currentTarget.value)
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                if (isConfirmed) {
                  handleConfirmClick();
                }
              }
            }}
            name="confirm-delete-domain"
            data-cy="confirm-delete-domain"
            className="w-full"
            required
            maxLength={150}
          />
          <div className="flex justify-center gap-16">
            <Modal.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Modal.Close>

            <Button
              variant="primary"
              loading={deleteDomain.isPending}
              disabled={!isConfirmed}
              onClick={handleConfirmClick}
            >
              Confirm
            </Button>
          </div>
        </div>
      </>
    </Modal.Content>
  );
};
